import { useMemo, useState } from 'react';
import { TableCell, TableRow, Box, Button } from '@mui/material';
import LabelMediaItem from '../Gallery/LabelMediaItem';
import useMediaHelper from '../MainComponent/hooks/useMediaHelper';
import ImageDialog from '../Gallery/ImageDialog';
import { downloadMultipleFiles } from '../../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/Store';
import { contextValue } from '../../constants';

type QuestionGalleryProps = {
  expanded?: boolean;
  question: string;
};

const QuestionGallery = ({ expanded, question }: QuestionGalleryProps) => {
  const {
    getAllImagesByQuestion,
    removeMediasByQuestion,
    getActiveLabelsForQuestion,
  } = useMediaHelper();

  const [visible, setVisible] = useState<boolean>(false);
  const { isReadOnly, queryObj } = useSelector(
    (state: RootState) => state.contextId
  );

  const activeLabels = useMemo(() => {
    const items = getActiveLabelsForQuestion(question);
    const resultWithMissingThumb = [];
    const resultWithCompleteThumb = [];

    for (const item of items) {
      const { Min = 0, Have = 0, Images }: any = item;
      const length = Math.max(Min, Have);

      // Convert Images to entries or default to an empty array
      const imageEntries = Images ? Object.entries(Images) : [];

      for (let i = 0; i < length; i++) {
        const [id, imageObj] = imageEntries[i] || [null, { Thumb: null }]; // Take the image at index `i` or use a default

        const thumb = {
          id,
          uri: ((imageObj || {}) as any).Thumb || '',
        };

        // Separate based on availability of Thumb.id and Thumb.uri
        if (!thumb.id || !thumb.uri) {
          resultWithMissingThumb.push({
            ...item,
            Thumb: thumb,
          });
        } else {
          resultWithCompleteThumb.push({
            ...item,
            Thumb: thumb,
          });
        }
      }
    }

    // Concatenate items with missing thumbs first, followed by items with complete thumbs
    return [...resultWithMissingThumb, ...resultWithCompleteThumb];
  }, [getActiveLabelsForQuestion, question]);

  const images = useMemo(() => {
    if (!visible) return [];
    return getAllImagesByQuestion(question);
  }, [visible, getAllImagesByQuestion, question]);

  if (!activeLabels || activeLabels.length === 0) return null;

  const removeAllLabels = (e: any) => {
    e?.stopPropagation();
    removeMediasByQuestion(question);
  };

  const downloadAllImages = (e: any) => {
    e?.stopPropagation();

    const files = getAllImagesByQuestion(question).map((e) => {
      return {
        url: e.webFileName,
        fileName: e.imageFileName,
      };
    });
    downloadMultipleFiles(files);
  };

  return (
    <>
      <Box sx={{ flexDirection: 'row' }}>
        <Button onClick={() => setVisible((prev) => !prev)}>View All</Button>
        {queryObj?.context !== contextValue?.AuditResultsBySubmissionId && (
          <>
            <Button
              onClick={downloadAllImages}
              disabled={isReadOnly}
              sx={{
                '&.Mui-disabled': {
                  cursor: 'not-allowed',
                  pointerEvents: 'all !important', // Override pointer-events: none
                },
              }}
            >
              Download All
            </Button>

            <Button
              onClick={removeAllLabels}
              disabled={isReadOnly}
              sx={{
                '&.Mui-disabled': {
                  cursor: 'not-allowed',
                  pointerEvents: 'all !important', // Override pointer-events: none
                },
              }}
            >
              Remove All
            </Button>
          </>
        )}
      </Box>

      <Box sx={{ width: '100%', padding: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 2,
            width: '100%',
            minWidth: 400,
          }}
        >
          {activeLabels.map((media: any, index) => (
            <Box key={index}>
              <LabelMediaItem
                item={media}
                index={index}
                type='question'
                expanded={true}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {visible && (
        <ImageDialog
          images={images}
          open={visible}
          onClose={() => setVisible(false)}
        />
      )}
    </>
  );
};

export default QuestionGallery;
